import { useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
// The pure class seam, not the component wrapper — the handle IS a <button> with slider
// semantics, so it needs the classes and not another element around them.
import { buttonClasses } from "@/components/design/button-classes";
import type { RankThresholds } from "./match-result-derive";

/**
 * The A/B/C thresholds as one three-handle slider — the control the CANDIDATE ranking has always
 * had, now shared with the company ranking.
 *
 * <p><b>Why it moved out of {@code CandidateRankingPanel} (review of spark!285, point 1).</b> The
 * company result set the same three thresholds with three number fields, so the same job had two
 * interaction models: users had to learn both, and every change to the rule cost two edits. The
 * reviewer asked for one component in both panels, and this is it.
 *
 * <p><b>CONTROLLED, and in fractions.</b> The value in and out is {@link RankThresholds} on 0..1,
 * because that is what both {@code companyBand} and the person clustering compare against — and a
 * component that owned the state would let the two panels drift apart again. Percent exists only
 * inside here, where the interaction lives: whole percents avoid the float drift that made three
 * separate 0..1 states awkward, and everything is snapped to {@link stepPct}.
 *
 * <p><b>The order is enforced, not merely displayed.</b> A ≥ B ≥ C with one step between each, so no
 * band can collapse and the handles cannot overtake one another. That is a property of the control
 * rather than of either caller, which is the other half of why it belongs here.
 *
 * <p><b>The scale runs right-to-left</b> (100 % on the left), inherited unchanged: the strongest band
 * sits where the eye starts. `left: 100 - value` is that inversion and the only thing to remember
 * when reading the geometry.
 */
export function RankingThresholdSlider({
  thresholds,
  onChange,
  stepPct = 5,
}: {
  thresholds: RankThresholds;
  onChange: (next: RankThresholds) => void;
  stepPct?: number;
}) {
  const intl = useIntl();

  const aPct = Math.round(thresholds.a * 100);
  const bPct = Math.round(thresholds.b * 100);
  const cPct = Math.round((thresholds.c ?? 0) * 100);

  const [activeThreshold, setActiveThreshold] = useState<ThresholdKey | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingThreshold = useRef<ThresholdKey | null>(null);

  function snapPct(clientX: number): number | null {
    // ASSERTED, NOT GUARDED. The ref is attached to the track div unconditionally, and every caller
    // of this is a pointer handler ON that div or on a handle inside it — so the element exists
    // whenever one of them runs. A `null` here would mean React dispatched an event from a subtree
    // it had already unmounted; probed rather than assumed, by unmounting and by re-attaching a
    // saved handle into a live container, and React dispatches nothing in either case.
    //
    // The width check below is the guard that DOES fire: a track that exists but has not been laid
    // out yet names no percentage, and that is a real state (a collapsed panel).
    const rect = trackRef.current!.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const raw = 100 - ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.min(100, Math.max(0, raw));
    return Math.round(clamped / stepPct) * stepPct;
  }

  function applyThreshold(key: ThresholdKey, pct: number) {
    const snapped = Math.round(pct / stepPct) * stepPct;
    if (key === "c") {
      const next = Math.min(Math.max(snapped, 0), Math.max(0, bPct - stepPct));
      onChange({ ...thresholds, c: next / 100 });
    } else if (key === "b") {
      const next = Math.min(Math.max(snapped, cPct + stepPct), aPct - stepPct);
      onChange({ ...thresholds, b: next / 100 });
    } else {
      const next = Math.max(Math.min(snapped, 100), Math.min(100, bPct + stepPct));
      onChange({ ...thresholds, a: next / 100 });
    }
  }

  function nearestThreshold(pct: number): ThresholdKey {
    // Ties go to the handle named first, which is the order the old sort settled them in too:
    // `>` keeps the incumbent when the distances are equal.
    let nearest: ThresholdKey = "c";
    let best = Math.abs(cPct - pct);
    if (Math.abs(bPct - pct) < best) {
      nearest = "b";
      best = Math.abs(bPct - pct);
    }
    if (Math.abs(aPct - pct) < best) nearest = "a";
    return nearest;
  }

  function startThresholdDrag(key: ThresholdKey, e: ReactPointerEvent<HTMLElement>) {
    e.preventDefault();
    e.stopPropagation();
    draggingThreshold.current = key;
    setActiveThreshold(key);
    e.currentTarget.setPointerCapture(e.pointerId);
    const pct = snapPct(e.clientX);
    if (pct !== null) applyThreshold(key, pct);
  }

  function moveThreshold(e: ReactPointerEvent<HTMLElement>) {
    const key = draggingThreshold.current;
    if (!key) return;
    e.preventDefault();
    const pct = snapPct(e.clientX);
    if (pct !== null) applyThreshold(key, pct);
  }

  function stopThresholdDrag(e: ReactPointerEvent<HTMLElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    draggingThreshold.current = null;
    setActiveThreshold(null);
  }

  const handles: Array<{
    key: ThresholdKey;
    cluster: "A" | "B" | "C";
    value: number;
    min: number;
    max: number;
    testid: string;
    aria: string;
  }> = [
    {
      key: "c",
      cluster: "C",
      value: cPct,
      min: 0,
      // Clamped exactly as applyThreshold clamps it: an advertised bound the control will not
      // honour is worse than no bound.
      max: Math.max(0, bPct - stepPct),
      testid: "ranking-threshold-c",
      aria: intl.formatMessage({
        id: "match.result.ranking.thresholdCAria",
        defaultMessage: "Untergrenze für C",
      }),
    },
    {
      key: "b",
      cluster: "B",
      value: bPct,
      min: cPct + stepPct,
      max: aPct - stepPct,
      testid: "ranking-threshold-b",
      aria: intl.formatMessage({
        id: "match.result.ranking.thresholdBAria",
        defaultMessage: "Schwelle zwischen B und C",
      }),
    },
    {
      key: "a",
      cluster: "A",
      value: aPct,
      min: Math.min(100, bPct + stepPct),
      max: 100,
      testid: "ranking-threshold-a",
      aria: intl.formatMessage({
        id: "match.result.ranking.thresholdAAria",
        defaultMessage: "Schwelle zwischen A und B",
      }),
    },
  ];

  return (
    <div className="mb-5 mt-3">
      <div className="flex items-center">
        <span className="shrink-0 text-smycles-label text-ink-muted">
          <FormattedMessage
            id="match.result.ranking.thresholdsLabel"
            defaultMessage="Schwellen"
          />
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <span className="shrink-0 text-smycles-micro tabular-nums text-ink-muted" aria-hidden="true">
          100 %
        </span>
        <div className="relative min-w-0 flex-1 px-6">
          <div
            ref={trackRef}
            className="relative h-16 cursor-pointer touch-none select-none"
            onPointerDown={(e) => {
              const pct = snapPct(e.clientX);
              if (pct !== null) startThresholdDrag(nearestThreshold(pct), e);
            }}
            onPointerMove={moveThreshold}
            onPointerUp={stopThresholdDrag}
            onPointerCancel={stopThresholdDrag}
          >
            <div
              className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
              style={{ background: "var(--color-accent)" }}
              aria-hidden="true"
            />
            {handles.map((h) => {
              const active = activeThreshold === h.key;
              return (
                <div
                  key={h.key}
                  className="absolute top-1/2 flex -translate-x-1/2 -translate-y-6 flex-col items-center gap-1"
                  style={{ left: `${100 - h.value}%`, zIndex: active ? 30 : 20 }}
                >
                  <button
                    type="button"
                    role="slider"
                    aria-valuemin={h.min}
                    aria-valuemax={h.max}
                    aria-valuenow={h.value}
                    aria-label={h.aria}
                    aria-valuetext={`${h.cluster} ${h.value} %`}
                    data-testid={h.testid}
                    onPointerDown={(e) => startThresholdDrag(h.key, e)}
                    onPointerMove={moveThreshold}
                    onPointerUp={stopThresholdDrag}
                    onPointerCancel={stopThresholdDrag}
                    onKeyDown={(e) => {
                      const step = e.shiftKey ? stepPct * 2 : stepPct;
                      if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
                        applyThreshold(h.key, h.value - step);
                      } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
                        applyThreshold(h.key, h.value + step);
                      } else if (e.key === "Home") {
                        applyThreshold(h.key, h.min);
                      } else if (e.key === "End") {
                        applyThreshold(h.key, h.max);
                      } else {
                        return;
                      }
                      e.preventDefault();
                    }}
                    className={buttonClasses({
                      variant: "primary",
                      size: "handle",
                      className: "cursor-grab font-semibold leading-none active:cursor-grabbing",
                    })}
                    style={
                      {
                        "--btn-fill": "var(--color-accent)",
                        "--btn-on-fill": "var(--color-on-accent)",
                        boxShadow: "none",
                        transform: active ? "scale(1.08)" : "scale(1)",
                        transitionTimingFunction: "cubic-bezier(.18,1.65,.35,1)",
                      } as CSSProperties
                    }
                  >
                    {/* Counter-scaled: the circle grows when grabbed, the letter stays optically put. */}
                    <span
                      className="inline-block transition"
                      style={{
                        transform: active ? "scale(calc(1 / 1.08))" : "scale(1)",
                        transitionTimingFunction: "cubic-bezier(.18,1.65,.35,1)",
                      }}
                    >
                      {h.cluster}
                    </span>
                  </button>
                  <span className="whitespace-nowrap text-smycles-micro tabular-nums text-ink-muted">
                    {h.value} %
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <span className="shrink-0 text-smycles-micro tabular-nums text-ink-muted" aria-hidden="true">
          0 %
        </span>
      </div>
    </div>
  );
}

type ThresholdKey = "a" | "b" | "c";
